/**
 * Activity Modal - TICKET ZKPVN1 + JXWPR9
 * 
 * Modal para crear/editar actividades.
 * Formulario condicional según tipo de actividad.
 * Incluye selector de refId para VRT/EPL (JXWPR9).
 */

import React, { useState, useEffect } from 'react';
import { X, AlertCircle, ExternalLink } from 'lucide-react';
import RefIdSelector from './RefIdSelector';

const ActivityModal = ({ activity, moduleId, existingIds, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    activityId: '',
    type: 'resource',
    title: '',
    estimatedMinutes: 5,
    refId: null,
    resource: {
      subtype: 'youtube',
      youtubeUrl: '',
      url: '',
      text: ''
    }
  });
  const [error, setError] = useState(null);
  const [showTypeWarning, setShowTypeWarning] = useState(false);
  const [showRefIdSelector, setShowRefIdSelector] = useState(false);

  useEffect(() => {
    if (activity) {
      setFormData({
        activityId: activity.activityId,
        type: activity.type,
        title: activity.title,
        estimatedMinutes: activity.estimatedMinutes,
        refId: activity.refId || null,
        resource: activity.resource || {
          subtype: 'youtube',
          youtubeUrl: '',
          url: '',
          text: ''
        }
      });
    }
  }, [activity]);

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  };

  const handleTypeChange = (newType) => {
    if (activity && newType !== formData.type) {
      setShowTypeWarning(true);
      setTimeout(() => setShowTypeWarning(false), 3000);
    }

    // Limpiar campos según tipo
    if (newType !== 'resource') {
      setFormData({
        ...formData,
        type: newType,
        resource: null,
        refId: null
      });
    } else {
      setFormData({
        ...formData,
        type: newType,
        resource: {
          subtype: 'youtube',
          youtubeUrl: '',
          url: '',
          text: ''
        },
        refId: null
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.title || formData.title.trim() === '') {
      setError('El título es requerido');
      return;
    }

    if (formData.estimatedMinutes === undefined || formData.estimatedMinutes < 0) {
      setError('Los minutos estimados deben ser >= 0');
      return;
    }

    // Validaciones específicas por tipo
    if (formData.type === 'resource') {
      if (!formData.resource || !formData.resource.subtype) {
        setError('Debes seleccionar un subtipo de recurso');
        return;
      }

      if (formData.resource.subtype === 'youtube') {
        if (!formData.resource.youtubeUrl || formData.resource.youtubeUrl.trim() === '') {
          setError('La URL de YouTube es requerida');
          return;
        }
      } else if (formData.resource.subtype === 'link') {
        if (!formData.resource.url || formData.resource.url.trim() === '') {
          setError('La URL del enlace es requerida');
          return;
        }
      } else if (formData.resource.subtype === 'text') {
        if (!formData.resource.text || formData.resource.text.trim() === '') {
          setError('El contenido de texto es requerido');
          return;
        }
      }
    }

    // Validar ID único (solo en creación)
    if (!activity) {
      const activityId = formData.activityId || generateSlug(formData.title);
      if (existingIds.includes(activityId)) {
        setError(`Ya existe una actividad con ID: ${activityId}`);
        return;
      }
    }

    // Construir payload limpio
    const payload = {
      activityId: formData.activityId,
      type: formData.type,
      title: formData.title.trim(),
      estimatedMinutes: parseInt(formData.estimatedMinutes, 10),
      refId: formData.refId,
      resource: formData.type === 'resource' ? formData.resource : null
    };

    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">
            {activity ? 'Editar actividad' : 'Crear actividad'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Type Change Warning */}
          {showTypeWarning && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
              ⚠️ Cambiar el tipo modificará la estructura de la actividad
            </div>
          )}

          {/* Activity ID (solo en creación) */}
          {!activity && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID de actividad <span className="text-gray-400">(opcional)</span>
              </label>
              <input
                type="text"
                value={formData.activityId}
                onChange={(e) => setFormData({ ...formData, activityId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="empathy-video"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si lo dejas vacío, se generará desde el título
              </p>
            </div>
          )}

          {/* Activity ID (readonly en edición) */}
          {activity && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID de actividad
              </label>
              <input
                type="text"
                value={formData.activityId}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
          )}

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de actividad *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="resource">Recurso (video, enlace, texto)</option>
              <option value="vrt_simulation">VRT Simulation</option>
              <option value="epl_lab">EPL Lab</option>
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Video: Entrevistas sin sesgo"
            />
          </div>

          {/* Estimated Minutes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minutos estimados *
            </label>
            <input
              type="number"
              required
              min="0"
              value={formData.estimatedMinutes}
              onChange={(e) => setFormData({ ...formData, estimatedMinutes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Campos específicos de RESOURCE */}
          {formData.type === 'resource' && formData.resource && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
              <h4 className="font-semibold text-blue-900">Configuración de Recurso</h4>

              {/* Subtype */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de recurso *
                </label>
                <select
                  value={formData.resource.subtype}
                  onChange={(e) => setFormData({
                    ...formData,
                    resource: { ...formData.resource, subtype: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="youtube">YouTube</option>
                  <option value="link">Enlace externo</option>
                  <option value="text">Texto</option>
                </select>
              </div>

              {/* YouTube URL */}
              {formData.resource.subtype === 'youtube' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de YouTube *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.resource.youtubeUrl}
                    onChange={(e) => setFormData({
                      ...formData,
                      resource: { ...formData.resource, youtubeUrl: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              )}

              {/* Link URL */}
              {formData.resource.subtype === 'link' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL del enlace *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.resource.url}
                    onChange={(e) => setFormData({
                      ...formData,
                      resource: { ...formData.resource, url: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    placeholder="https://..."
                  />
                </div>
              )}

              {/* Text content */}
              {formData.resource.subtype === 'text' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contenido de texto *
                  </label>
                  <textarea
                    required
                    value={formData.resource.text}
                    onChange={(e) => setFormData({
                      ...formData,
                      resource: { ...formData.resource, text: e.target.value }
                    })}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white"
                    placeholder="Escribe aquí el contenido educativo..."
                  />
                </div>
              )}
            </div>
          )}

          {/* Placeholder para VRT/EPL (refId será en ticket futuro) */}
          {(formData.type === 'vrt_simulation' || formData.type === 'epl_lab') && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
              <h4 className="font-semibold text-purple-900">
                {formData.type === 'vrt_simulation' ? 'Agente VRT' : 'Lab EPL'}
              </h4>
              
              {/* Estado actual del refId */}
              {formData.refId ? (
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-300">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Seleccionado:</p>
                    <p className="text-xs text-gray-600 font-mono mt-1">{formData.refId}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowRefIdSelector(true)}
                    className="px-3 py-1.5 text-sm text-purple-700 hover:bg-purple-100 rounded transition-colors"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-yellow-800 font-medium">No configurado</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Esta actividad requiere asignar un {formData.type === 'vrt_simulation' ? 'agente VRT' : 'lab EPL'} antes de publicar el curso.
                    </p>
                  </div>
                </div>
              )}

              {/* Botón seleccionar */}
              <button
                type="button"
                onClick={() => setShowRefIdSelector(true)}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {formData.refId ? 'Cambiar selección' : 'Seleccionar ' + (formData.type === 'vrt_simulation' ? 'agente VRT' : 'lab EPL')}
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {activity ? 'Guardar cambios' : 'Crear actividad'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal selector de refId */}
      {showRefIdSelector && (
        <RefIdSelector
          type={formData.type}
          currentRefId={formData.refId}
          onSelect={(refId) => {
            setFormData({ ...formData, refId });
            setShowRefIdSelector(false);
          }}
          onClose={() => setShowRefIdSelector(false)}
        />
      )}
    </div>
  );
};

export default ActivityModal;
